import { vnTemplate } from '@valnet/vnKnockoutJsTemplates/vnTemplate';
import { closeDropdownPanel } from '~/ui/dropdown';
import { legacyIconNameToPhosphorIconName } from '~/utils/iconName';
let listPageSelectMountRegistered = false;
/**
 * Mount a listPageSelect the same way getHtmlBuild works on static screens,
 * but for dynamic hosts (foreach pages, modals, etc.).
 */
export function registerListPageSelectMountBinding() {
    if (listPageSelectMountRegistered || typeof ko === 'undefined' || !ko.bindingHandlers) {
        return;
    }
    listPageSelectMountRegistered = true;
    ko.bindingHandlers.listPageSelectMount = {
        init(element, valueAccessor, _allBindings, _viewModel, bindingContext) {
            const select = ko.unwrap(valueAccessor());
            if (!select) {
                return { controlsDescendantBindings: true };
            }
            element.innerHTML = select.getHtmlBuild();
            const host = element.firstElementChild;
            if (!(host instanceof HTMLElement)) {
                return { controlsDescendantBindings: true };
            }
            // getHtmlBuild expects `data: <idElement>` on the parent VM.
            ko.applyBindingsToDescendants(bindingContext.createChildContext(select.viewModel), element);
            // Live DOM node (getHtmlBuild's serialized node is detached).
            select.builtTemplate = host;
            return { controlsDescendantBindings: true };
        },
    };
}
export class listPageSelect_template extends vnTemplate {
    constructor() {
        super(...arguments);
        this.valueBinding = null;
        this.valuesBinding = null;
        this.syncing = false;
        this.suppressChange = false;
        this.applyOnChange = true;
        this.isMulti = false;
    }
    getTemplateName() {
        return 'listPageSelect-template';
    }
    getMainElementClasses() {
        return [];
    }
    buildViewModel() {
        const self = this;
        const vm = {
            label: ko.observable(''),
            options: ko.observableArray([]),
            searchTerm: ko.observable('').extend({ rateLimit: { timeout: 200, method: 'notifyWhenChangesStop' } }),
            searchEnabled: ko.observable(false),
            searchPlaceholder: ko.observable('Search'),
            selectedOption: ko.observable(''),
            selectedOptions: ko.observableArray([]),
            isMulti: ko.observable(false),
            elementIdentifier: ko.observable(this.idElement),
            isDisabled: ko.observable(false),
            isFormField: ko.observable(false),
            isPlainList: ko.observable(false),
            iconName: ko.observable(''),
            showLabelPrefix: ko.observable(false),
            onSelectOption: function (option) {
                self.onSelectOption(option);
            },
            isOptionSelected: function (optionId) {
                if (vm.isMulti()) {
                    return vm.selectedOptions.indexOf(String(optionId)) !== -1;
                }
                return vm.selectedOption() === String(optionId);
            },
            onSearchClick: function (_data, event) {
                event.stopPropagation();
                return true;
            },
            onSearchInput: function (_data, event) {
                const target = event.target;
                if (target instanceof HTMLInputElement) {
                    vm.searchTerm(target.value);
                }
                return true;
            },
        };
        vm.selectedMatch = ko.pureComputed(function () {
            if (vm.isMulti()) {
                return null;
            }
            const selected = String(vm.selectedOption() ?? '');
            const match = vm.options().find((option) => option.id === selected);
            return match || null;
        });
        vm.showSelectedPrefix = ko.pureComputed(function () {
            if (!vm.showLabelPrefix() || vm.isMulti() || !vm.label()) {
                return false;
            }
            const match = vm.selectedMatch();
            if (!match) {
                return false;
            }
            const optionName = String(match.name || '').trim().toLowerCase();
            const labelName = String(vm.label() || '').trim().toLowerCase();
            return optionName !== '' && optionName !== labelName;
        });
        vm.selectedPrefix = ko.pureComputed(function () {
            if (!vm.showSelectedPrefix()) {
                return '';
            }
            return String(vm.label()).replace(/\s*:\s*$/, '') + ' :';
        });
        vm.displayValue = ko.pureComputed(function () {
            if (vm.isMulti()) {
                const selectedIds = vm.selectedOptions() || [];
                if (!selectedIds.length) {
                    return vm.label() || 'Select';
                }
                const names = selectedIds
                    .map((id) => {
                    const match = vm.options().find((option) => option.id === id);
                    return match ? match.name : '';
                })
                    .filter(Boolean);
                if (!names.length) {
                    return vm.label() || 'Select';
                }
                if (names.length === 1) {
                    return names[0];
                }
                if (names.length === 2) {
                    return names.join(', ');
                }
                return names[0] + ' +' + (names.length - 1);
            }
            const match = vm.selectedMatch();
            if (match) {
                return match.name;
            }
            return vm.label();
        });
        vm.displayLabel = vm.displayValue;
        vm.selectedOptionClass = ko.pureComputed(function () {
            if (vm.isMulti()) {
                return '';
            }
            const match = vm.selectedMatch();
            return match && match.optionClass ? match.optionClass : '';
        });
        vm.panelTitle = ko.pureComputed(function () {
            const label = String(vm.label() || '').trim();
            if (!label) {
                return 'Filter';
            }
            const normalizedLabel = label.charAt(0).toUpperCase() + label.slice(1);
            return 'Filter ' + normalizedLabel;
        });
        vm.visibleOptions = ko.pureComputed(function () {
            const search = String(vm.searchTerm() || '').trim().toLowerCase();
            const options = vm.searchEnabled()
                ? vm.options().filter((option) => option.id !== '')
                : vm.options();
            if (!vm.searchEnabled() || search === '') {
                return options;
            }
            return options.filter((option) => {
                return String(option.name || '').toLowerCase().indexOf(search) !== -1;
            });
        });
        return vm;
    }
    getTemplateHtml() {
        return `
            <div
                class="dropdown lib-dropdown lp-dropdown-select list-page-select"
                data-bind="
                    attr: { id: 'listPageSelect_' + elementIdentifier() },
                    css: {
                        'list-page-select--form': isFormField,
                        'list-page-select--multi': isMulti,
                        'list-page-select--plain': isPlainList
                    }
                "
            >
                <button type="button" class="dropdown-toggle icon" data-bind="disable: isDisabled">
                    <!-- ko if: iconName -->
                    <i
                        data-lp-component="Icon"
                        data-icon-weight="regular"
                        class="lp-icon ph dropdown-toggle__icon"
                        data-bind="css: 'ph-' + iconName()"
                        aria-hidden="true"
                    ></i>
                    <!-- /ko -->
                    <span class="dropdown-toggle__label">
                        <!-- ko if: showSelectedPrefix -->
                        <span class="dropdown-toggle__prefix" data-bind="text: selectedPrefix"></span>
                        <!-- /ko -->
                        <span class="dropdown-toggle__value" data-bind="text: displayValue, css: selectedOptionClass"></span>
                    </span>
                </button>
                <div class="dropdown-list">
                    <div class="w-multi-dropdown">
                        <p class="multi-dropdown-title" data-bind="text: panelTitle"></p>
                        <div class="multi-dropdown-body">
                            <div class="list-page-select-search" data-bind="visible: searchEnabled, event: { click: onSearchClick }">
                                <input
                                    class="lp-select-search list-page-select-search__input"
                                    type="search"
                                    data-bind="
                                        value: searchTerm,
                                        valueUpdate: 'afterkeydown',
                                        event: { input: onSearchInput },
                                        attr: { placeholder: searchPlaceholder }
                                    "
                                />
                            </div>
                            <ul class="multi-dropdown-list">
                                <!-- ko foreach: visibleOptions -->
                                <li
                                    class="multi-dropdown-item"
                                    data-bind="
                                        css: { 'is-selected': $parent.isOptionSelected(id) },
                                        click: function () {
                                            if ($parent.isMulti()) {
                                                return true;
                                            }
                                            $parent.onSelectOption($data);
                                        }
                                    "
                                >
                                    <label class="list-page-select-option">
                                        <input
                                            class="list-page-select-option__input"
                                            data-bind="
                                                attr: {
                                                    type: $parent.isMulti() ? 'checkbox' : 'radio',
                                                    id: 'listPageSelect_option_' + $parent.elementIdentifier() + '_' + ($data.id === '' ? 'empty' : id),
                                                    name: 'listPageSelect_' + $parent.elementIdentifier()
                                                },
                                                checked: $parent.isMulti() ? $parent.selectedOptions : $parent.selectedOption,
                                                checkedValue: id,
                                                disable: $parent.isDisabled() || disabled,
                                                click: function () { return true; },
                                                clickBubble: false
                                            "
                                        />
                                        <span class="list-page-select-option__text">
                                            <span data-bind="text: name, css: optionClass"></span>
                                        </span>
                                    </label>
                                </li>
                                <!-- /ko -->
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
    includeTemplate() {
        const existing = document.getElementById(this.getTemplateName());
        if (existing instanceof HTMLScriptElement) {
            existing.innerHTML = this.template.innerHTML;
            return;
        }
        super.includeTemplate();
    }
    setLabel(label) {
        this.getTemplateViewModel().label(label);
    }
    /**
     * When true, a selected value is shown as "Label : Value" in the closed toggle.
     */
    setShowLabelPrefix(enabled) {
        this.getTemplateViewModel().showLabelPrefix(!!enabled);
    }
    setIcon(symbolId) {
        this.setIconName(symbolId ? legacyIconNameToPhosphorIconName(symbolId) : '');
    }
    setIconName(iconName) {
        this.getTemplateViewModel().iconName(iconName || '');
    }
    /**
     * Enable multi-select (checkboxes). Call before bindValues().
     */
    setMulti(enabled) {
        this.isMulti = !!enabled;
        this.getTemplateViewModel().isMulti(this.isMulti);
        if (this.isMulti) {
            this.setApplyOnChange(false);
        }
    }
    /**
     * When false, selecting an option only updates the bound value (form fields).
     * When true (default), also triggers list filter reload hooks.
     */
    setApplyOnChange(enabled) {
        this.applyOnChange = !!enabled;
    }
    /**
     * Stretch the control to fill a form-group / grid cell.
     */
    setFormField(enabled) {
        this.getTemplateViewModel().isFormField(!!enabled);
        if (enabled) {
            this.setApplyOnChange(false);
        }
    }
    setSearchEnabled(enabled, placeholder = 'Search') {
        const vm = this.getTemplateViewModel();
        vm.searchEnabled(!!enabled);
        vm.searchPlaceholder(placeholder);
        if (!enabled) {
            vm.searchTerm('');
        }
    }
    setPlainList(enabled) {
        this.getTemplateViewModel().isPlainList(!!enabled);
    }
    setOptions(options) {
        const normalized = (options || []).map((option) => ({
            id: String(option.id),
            name: option.name,
            disabled: !!option.disabled,
            optionClass: option.optionClass || '',
        }));
        this.suppressChange = true;
        this.getTemplateViewModel().options(normalized);
        this.suppressChange = false;
    }
    setSelectedOption(optionId) {
        this.suppressChange = true;
        this.getTemplateViewModel().selectedOption(optionId === null || typeof optionId === 'undefined' ? '' : String(optionId));
        this.suppressChange = false;
    }
    setSelectedOptions(optionIds) {
        const normalized = (optionIds || []).map((id) => String(id));
        this.suppressChange = true;
        this.getTemplateViewModel().selectedOptions(normalized);
        this.suppressChange = false;
    }
    getSelectedOption() {
        const vm = this.getTemplateViewModel();
        const selected = vm.selectedOption();
        return vm.options().find((option) => option.id === selected) || null;
    }
    getSelectedValue() {
        return this.getTemplateViewModel().selectedOption();
    }
    getSelectedValues() {
        return (this.getTemplateViewModel().selectedOptions() || []).slice();
    }
    bindValue(target) {
        this.valueBinding = target;
        const vm = this.getTemplateViewModel();
        vm.selectedOption.subscribe((value) => {
            if (this.syncing || this.suppressChange) {
                return;
            }
            this.syncing = true;
            target(value);
            this.syncing = false;
        });
        target.subscribe?.((value) => {
            if (this.syncing || this.suppressChange) {
                return;
            }
            this.syncing = true;
            vm.selectedOption(value === null || typeof value === 'undefined' ? '' : String(value));
            this.syncing = false;
        });
        const initial = target();
        vm.selectedOption(initial === null || typeof initial === 'undefined' ? '' : String(initial));
    }
    /**
     * Two-way bind an observableArray of selected option ids (multi mode).
     */
    bindValues(target) {
        this.valuesBinding = target;
        const vm = this.getTemplateViewModel();
        vm.selectedOptions.subscribe((values) => {
            if (this.syncing || this.suppressChange) {
                return;
            }
            this.syncing = true;
            target((values || []).map((id) => String(id)));
            this.syncing = false;
        });
        target.subscribe?.((values) => {
            if (this.syncing || this.suppressChange) {
                return;
            }
            this.syncing = true;
            vm.selectedOptions((values || []).map((id) => String(id)));
            this.syncing = false;
        });
        const initial = target() || [];
        vm.selectedOptions(initial.map((id) => String(id)));
    }
    setDisable(disabled) {
        this.getTemplateViewModel().isDisabled(!!disabled);
    }
    onSelectOption(option) {
        if (this.suppressChange || option.disabled || this.getTemplateViewModel().isDisabled()) {
            return;
        }
        const vm = this.getTemplateViewModel();
        // Multi mode is driven by checkbox `checked` / `checkedValue` bindings.
        if (vm.isMulti()) {
            return;
        }
        const nextValue = String(option.id);
        const changed = vm.selectedOption() !== nextValue;
        vm.selectedOption(nextValue);
        if (changed) {
            this.onOptionChange();
            return;
        }
        this.closeDropdown();
    }
    onOptionChange() {
        if (this.suppressChange) {
            return;
        }
        this.closeDropdown();
        if (this.applyOnChange) {
            this.applyFilter();
        }
    }
    applyFilter() {
        const caller = this.caller;
        if (typeof caller.filterOnChange === 'function') {
            caller.filterOnChange();
            return;
        }
        if (typeof caller.filterDropdownOnChange === 'function') {
            caller.filterDropdownOnChange();
            return;
        }
        if (typeof caller.getListData === 'function') {
            caller.getListData();
        }
    }
    closeDropdown() {
        const dropdownId = 'listPageSelect_' + this.idElement;
        let dropdown = document.getElementById(dropdownId);
        if (!(dropdown instanceof HTMLElement) && this.builtTemplate instanceof HTMLElement) {
            dropdown = this.builtTemplate.id === dropdownId
                ? this.builtTemplate
                : this.builtTemplate.querySelector(`#${cssEscapeId(dropdownId)}, .dropdown.list-page-select, .list-page-select`);
        }
        if (!(dropdown instanceof HTMLElement)) {
            return;
        }
        closeDropdownPanel(dropdown);
        dropdown.querySelector('.dropdown-toggle')?.setAttribute('aria-expanded', 'false');
    }
}
function cssEscapeId(id) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(id);
    }
    return id.replace(/([^a-zA-Z0-9_-])/g, '\\$1');
}
